<template>
  <v-dialog v-model="shown" :max-width="600" persistent>
    <v-card>
      <v-responsive>
        <v-card-title class="d-flex flex-column flex-sm-row align-start align-sm-center px-6 pb-0 pt-6">
          <h3 class="mb-2 mb-sm-0" style="word-break: keep-all !important; overflow-wrap: normal !important; white-space: normal !important; hyphens: none !important;">
            {{ $t("signflow.EditSigner") }}
          </h3>
        </v-card-title>
        <v-card-text class="px-0 pt-4 pb-6">
          <v-card class="pa-2 mt-0 mb-0" color="#ffffff" flat>
            <v-form v-if="editableSigner" v-model="formValid">
              <div shio-sign-card-input mx-auto>
                <v-text-field
                    class="shio-sign-card-input mx-auto"
                    v-model="editableSigner.name"
                    :rules="[ validateFullName, validateSignerName ]"
                    :label="$t('signflow.SignersName')"
                    required
                ></v-text-field>
                <v-tooltip bottom nudge-top="35" :disabled="!isEmailDisabled || $vuetify.breakpoint.smAndDown">
                  <template v-slot:activator="{ on, attrs }">
                    <div v-bind="attrs" v-on="on">
                      <v-text-field
                          class="shio-sign-card-input mx-auto"
                          v-model="editableSigner.email"
                          :rules="[ validateEmail, signerIsNew, signerIsNotMe ]"
                          :label="$t('signflow.SignersEmail')"
                          :disabled="isEmailDisabled"
                          :hint="(isEmailDisabled && $vuetify.breakpoint.smAndDown) ? $t('signflow.InsufficientFreeTrialInvitations') : ''"
                          :persistent-hint="isEmailDisabled && $vuetify.breakpoint.smAndDown"
                          required
                      ></v-text-field>
                    </div>
                  </template>
                  <span>{{ $t("signflow.InsufficientFreeTrialInvitations") }}</span>
                </v-tooltip>
                <p v-if="!isEmailDisabled" class="text-left pt-7" style="width: 80%; margin-left: 10%; color: #666666;">
                  <v-icon style="font-size: 20px; vertical-align: text-bottom;" class="mr-1 alert-info-icon">mdi-alert-circle</v-icon>{{ $t( "signflow.UpdateSignerAlert" ) }}
                </p>
              </div>
            </v-form>
          </v-card>
        </v-card-text>
        <v-card-actions class="px-6 pb-4 pt-0">
          <v-btn
              @click="onCancel"
              :class="$vuetify.breakpoint.smAndUp ? 'secondary-btn' : ''"
              :style="$vuetify.breakpoint.smAndUp ? { width: '120px !important', minWidth: '120px !important', padding: '0 !important' } : {}"
          >
            {{ $t("signflow.Cancel") }}
          </v-btn>
          <v-spacer></v-spacer>
          <v-btn
              @click="onUpdate"
              color="primary"
              :class="$vuetify.breakpoint.smAndUp ? 'primary-btn' : ''"
              :disabled="!isChanged || !isValid"
              :loading="isSaving"
              :style="$vuetify.breakpoint.smAndUp ? { width: '120px !important', minWidth: '120px !important', padding: '0 !important' } : {}"
          >
            {{ $t("signflow.Confirm") }}
          </v-btn>
        </v-card-actions>
      </v-responsive>
    </v-card>
  </v-dialog>
</template>

<script>
    import util from "../../../../util/util";

    export default {
        name : "UpdateSignerDialog",
        props : {
            resolve : Function,
            flow : Object,
            signer : Object,
        },
        data()
        {
            return {
                formValid : false,
                shown : false,
                editableSigner : null,
                originalSigner : null, // To track changes
                otherSigners : [],
                isSaving : false
            }
        },
        computed : {
            /**
             * Disables email editing if the user is on a trial and out of free invitations.
             * * @returns {boolean}
             */
            isEmailDisabled()
            {
                if ( this.flow && this.flow.signatureLevel === 'advanced' )
                {
                    return false;
                }
                const stripeOn = $cc_user_info.stripePaymentOn;
                const hasSub = $cc_user_info.subscription && $cc_user_info.subscription.has_valid_subscription;
                const invitesLeft = $cc_user_info.organization ? $cc_user_info.organization.free_basic_invitations : 0;
                if ( !stripeOn || hasSub )
                {
                    return false;
                }
                return invitesLeft <= 0;
            },
            /**
             * Checks if the data has been changed from its original state.
             */
            isChanged()
            {
                if( !this.editableSigner || !this.originalSigner )
                {
                    return false;
                }
                return (
                this.editableSigner.name !== this.originalSigner.name ||
                this.editableSigner.email !== this.originalSigner.email
                );
            },
            /**
             * Basic validation to ensure fields are not empty.
             */
            isValid()
            {
                return this.editableSigner
                       && this.editableSigner.name.trim()
                       !== ''
                       && this.editableSigner.email.trim()
                       !== '' && this.editableSigner.email.trim() !== $cc_user_info.email
                       && this.formValid;
            },
            /**
             * Checks whether the email is me user's.
             *
             * @returns {boolean|*}
             */
            signerIsNotMe()
            {
                return !(this.editableSigner.email.trim().toLowerCase() === $cc_user_info.email.trim().toLowerCase())
                       || this.$t( "message.MustNotBeMeEmail" );
            },
            /**
             * Checks whether the email is unique in all signers.
             *
             * @returns {boolean|*}
             */
            signerIsNew()
            {
                return !this._signerExists( this.editableSigner.email ) || this.$t( "message.MustBeUniqueEmail" );
            }
        },
        methods : {
            /**
             * Full name validator.
             */
            validateFullName : util.validateFullName,
            /**
             * Email validator.
             */
            validateEmail : util.validateEmail,
            /**
             * Signer name validator.
             */
            validateSignerName( value )
            {
                return( !value || util.goodRegExp.test( value ) || this.$t( "message.EnterValidCharacters" ) );
            },
            /**
             * Emits 'close' to tell the parent component to close this pane.
             */
            onCancel()
            {
                this.resolve( false );
            },
            /**
             * Submits the updated signer information to the server.
             */
            onUpdate()
            {
                if( !this.isChanged || !this.isValid )
                {
                    return;
                }
                this.isSaving = true;
                const payload = {
                    signerName : this.editableSigner.name,
                    signerEmail : this.editableSigner.email,
                };
                this.$store.signFlowClient.updateSignerFromSignflow( this.flow.groupId,
                this.flow.uuid,
                this.signer.uuid,
                payload )
                .then( () =>
                {
                    this.resolve( true );
                } )
                .catch( ( e ) =>
                {
                    this.$parent.$error( this.$t( "signflow.Error!Unexpected" ) );
                } )
                .finally( () =>
                {
                    this.isSaving = false;
                } );
            },
            /**
             * Cancel signer update.
             */
            cancel()
            {
                this.resolve( false );
            },
            /**
             * Check if the new email exists in other signers'
             *
             * @returns {boolean}
             */
            _signerExists( email )
            {
                if( !email )
                {
                    return false;
                }
                else
                {
                    const mails = this.otherSigners.filter( signer => signer.signerEmail.trim().toLowerCase()
                                                                      === email.trim().toLowerCase() );
                    return (mails.length > 0);
                }
            }
        },
        mounted()
        {
            this.shown = true;
            if( this.signer )
            {
                this.editableSigner = { name : this.signer.signerName, email : this.signer.signerEmail };
                this.originalSigner = { name : this.signer.signerName, email : this.signer.signerEmail };
                this.otherSigners =
                this.flow.signers.filter( signer => signer.signerEmail !== this.signer.signerEmail );
            }
        }
    }
</script>

<style lang="sass" scoped>
@import "../../../../styles/style"

.vdp-message
  white-space: pre-wrap

.theme--light.v-btn.v-btn--disabled
  background-color: #ff507a !important
  color: white !important
  opacity: .5

.shio-sign-card-input
  width: 90%
  margin: 0 auto

.alert-info-icon
  color: $dark-grey !important

// Mobile specific overrides
@media (max-width: 599px)
  .shio-sign-card-input
    width: 100% !important
</style>
